import type {
  LedgerLine,
  ManualPaymentMethod,
  MembershipStatus,
  OwedBalance,
  OwnerParticipant,
  OwnerPaymentRequest,
  UpcomingBooking,
} from "@teamna/shared";
import { formatDayKey, isSessionFull, membershipOfferLine, newClientsOnlyLabel, packPeriodLine, payLinkMessage } from "@teamna/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  ButtonLink,
  CellField,
  CopyIcon,
  ChipRow,
  EmptyState,
  ErrorState,
  Field,
  InlineError,
  Notice,
  Panel,
  RadioGroup,
  Select,
  SkeletonList,
  showToast,
  type DataTableColumn,
} from "../../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import {
  useAdjustSessions,
  useBusinessCancelBooking,
  useCancelPaymentRequest,
  useCreatePaymentRequest,
  useOwnerSessions,
  useRecordPayment,
  usePackageStart,
  useSellPackage,
  useUpdateMembership,
} from "@teamna/web-client/hooks/use-owner.ts";
import { isApiError, newIdempotencyKey } from "@teamna/web-client/api/client.ts";
import { useAddParticipantToSession } from "@teamna/web-client/hooks/use-groups.ts";
import { usePackagesAndRules } from "@teamna/web-client/hooks/use-setup.ts";
import { formatDate, formatDateTime, formatMoney } from "@teamna/web-client/lib/format.ts";
import { Stepper } from "../setup/frame.tsx";
import { ProofField, useProofUpload } from "./proof-field.tsx";

/**
 * The panels of a parent's page (owner menu revamp, 2026-10-09). They came from the member
 * profile (web-app-v2 slice D) unchanged: each one is a single action on one family or one child.
 */

export function MembershipPanel({
  businessId,
  membershipId,
  name,
  status,
  onClose,
}: {
  businessId: string;
  membershipId: string;
  name: string;
  status: MembershipStatus;
  onClose: () => void;
}) {
  const update = useUpdateMembership(businessId);
  const title = status === "left" ? `Remove ${name}?` : status === "paused" ? `Pause ${name}?` : `Resume ${name}?`;
  const what =
    status === "left"
      ? "They lose access to this business's schedule. Their past bookings and payments stay on record. You can invite them back with the same code."
      : status === "paused"
        ? "They keep their account and go out of your active list. Their balances stay as they are."
        : "They get access to this business's schedule again.";
  return (
    <Panel
      open
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button
            variant={status === "left" ? "danger" : "primary"}
            loading={update.isPending}
            onClick={() =>
              update.mutate(
                { membershipId, status },
                {
                  onSuccess: () => {
                    showToast(status === "left" ? `${name} removed` : status === "paused" ? `${name} paused` : `${name} resumed`);
                    onClose();
                  },
                },
              )
            }
          >
            {status === "left" ? "Remove" : status === "paused" ? "Pause" : "Resume"}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <p className="text-[15px] text-ink-2">{what}</p>
      {update.error ? (
        <div className="mt-3">
          <InlineError error={update.error} />
        </div>
      ) : null}
    </Panel>
  );
}

/** Mobile spec 4.3: the fixed reasons per direction. The member reads the one chosen. */
export const ADD_REASONS = ["Courtesy", "Paid in cash", "Correction", "We cancelled"];
export const REMOVE_REASONS = ["Correction", "No-show", "Used elsewhere"];

export function AdjustPanel({
  businessId,
  participantId,
  name,
  memberName,
  direction,
  current,
  onClose,
}: {
  businessId: string;
  participantId: string;
  name: string;
  memberName: string;
  direction: "add" | "remove";
  current: number;
  onClose: () => void;
}) {
  const reasons = direction === "add" ? ADD_REASONS : REMOVE_REASONS;
  const [amount, setAmount] = useState(1);
  // Correction comes chosen in both directions (Anas, 2026-10-29): most changes by hand fix a count.
  const [reason, setReason] = useState<string>("Correction");
  const [note, setNote] = useState("");
  const adjust = useAdjustSessions(businessId);
  const max = direction === "add" ? 12 : Math.max(1, Math.min(12, current));
  const after = direction === "add" ? current + amount : Math.max(0, current - amount);
  const shown = note.trim() ? `${reason} (${note.trim()})` : reason;

  return (
    <Panel
      open
      onClose={onClose}
      title={direction === "add" ? `Add sessions for ${name}` : `Remove sessions from ${name}`}
      actions={
        <>
          <Button
            loading={adjust.isPending}
            onClick={() =>
              adjust.mutate(
                { participantId, direction, amount, reason, ...(note.trim() ? { note: note.trim() } : {}) },
                {
                  onSuccess: (profile) => {
                    const left = profile.balances.reduce((total, row) => total + row.sessionsLeft, 0);
                    showToast(`${name} has ${left} ${left === 1 ? "session" : "sessions"} left`);
                    onClose();
                  },
                },
              )
            }
          >
            Apply
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[15px] font-semibold">How many</span>
          <Stepper value={amount} onChange={setAmount} min={1} max={max} label={`Sessions to ${direction === "add" ? "add" : "remove"}`} />
        </div>
        <ChipRow
          options={reasons.map((value) => ({ id: value, label: value }))}
          selectedId={reason}
          onSelect={setReason}
          label="Reason"
          wrap
        />
        <Field
          label="Note, optional"
          value={note}
          maxLength={200}
          placeholder="Closed on Tuesday"
          onChange={(event) => setNote(event.target.value)}
        />
        <p className="rounded-card bg-accent-surface px-4 py-3 text-[15px] text-ink" aria-live="polite">
          {name} goes from {current} to {after} {after === 1 ? "session" : "sessions"}. Reason shown to {memberName}: {shown}.
        </p>
        {adjust.error ? <InlineError error={adjust.error} /> : null}
      </div>
    </Panel>
  );
}

type PaymentChoice = ManualPaymentMethod | "later";

const PAID_WORDS: Record<ManualPaymentMethod, string> = { cash: "in cash", transfer: "by bank transfer" };

/**
 * A new package at the desk (product-intent 3.4), the "A new package" step of Record a payment
 * (`record-payment.tsx`, #ai-feedback 2026-10-26 card #5). The owner picks the package and how the
 * member paid: cash, bank transfer, or not yet. Paid records the money in Payments with no fee, with
 * an optional proof of payment (card #2). Not yet leaves the package owed, and the member can pay
 * it in the app or the owner records it later.
 *
 * One idempotency key per opening of the panel: a second press, or a resend after a lost answer,
 * replays the first sale instead of selling the package twice.
 *
 * An intro offer ("New clients only", migration 0070) sells at once to a new client. For a person
 * who bought or came here before, the API refuses with NEW_CLIENTS_ONLY; the panel then says so in
 * a warning box, and "Add it anyway" sends the sale again with the override. The audit row of the
 * sale records the override. A refused sale frees its key, so the second send uses the same one.
 *
 * A package limited to some groups shows only for a child in one of them (2026-10-30: a Gold Team
 * swimmer was offered the Learn To Swim packages). The others wait behind "Show packages for other
 * levels", for a child who moves up. A child with no group sees every package.
 */
/** One thing the desk can sell: a pack, or a monthly membership (M2) with its first month. */
type SellOption = { id: string; name: string; priceFils: number; newClientsOnly: boolean; hint: string; credits: number; unlimited: boolean; membership: boolean; groupIds: string[] | null };

export function SellPackagePanel({
  businessId,
  participantId,
  groupId = null,
  name,
  memberName,
  onBack,
  animate = true,
  onClose,
}: {
  businessId: string;
  participantId: string;
  /** The child's group: packages for other groups wait behind a button. */
  groupId?: string | null;
  name: string;
  memberName: string;
  /** Back to "What is the payment for?", when the panel asked it first. */
  onBack?: (() => void) | undefined;
  /** False when the panel is the next step of a panel that is open already. */
  animate?: boolean;
  onClose: () => void;
}) {
  const { label: word } = useLabels();
  const catalog = usePackagesAndRules(businessId);
  const sell = useSellPackage(businessId);
  const proof = useProofUpload(businessId);
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const packages = useMemo<SellOption[]>(() => {
    if (!catalog.data) return [];
    const packs: SellOption[] = catalog.data.fromTemplate
      ? []
      : catalog.data.packages
          .filter((row) => row.active)
          .sort((a, b) => Number(a.kind === "private") - Number(b.kind === "private") || a.sessionsCount - b.sessionsCount)
          .map((row) => ({
            id: row.id,
            name: row.name,
            priceFils: row.priceFils,
            newClientsOnly: row.newClientsOnly ?? false,
            hint: `${row.sessionsCount} ${row.sessionsCount === 1 ? "session" : "sessions"}, ${row.priceFils === 0 ? "free" : formatMoney(row.priceFils)}, valid ${
              row.validityDays
            } days${row.newClientsOnly ? `. ${newClientsOnlyLabel(word("member", { plural: true }))}` : ""}`,
            credits: row.sessionsCount,
            unlimited: false,
            membership: false,
            groupIds: row.groupIds ?? null,
          }));
    // A monthly membership (M2): the first month starts today and costs the full price.
    const months: SellOption[] = (catalog.data.memberships ?? []).map((row) => {
      const cap = row.unlimited ? null : row.credits;
      return {
        id: row.id,
        name: row.name,
        priceFils: row.priceFils,
        newClientsOnly: false,
        hint: `${membershipOfferLine({ cap, days: row.validityDays })}. ${formatMoney(row.priceFils)} a month`,
        credits: row.credits,
        unlimited: row.unlimited ?? false,
        membership: true,
        groupIds: row.groupIds,
      };
    });
    return [...packs, ...months];
  }, [catalog.data, word]);
  const [productId, setProductId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const fits = (row: SellOption) => groupId === null || row.groupIds === null || row.groupIds.length === 0 || row.groupIds.includes(groupId);
  const otherCount = packages.filter((row) => !fits(row)).length;
  // The chosen package stays in the list, also when it is for another group.
  const shown = showAll ? packages : packages.filter((row) => fits(row) || row.id === productId);
  const [payment, setPayment] = useState<PaymentChoice>("cash");
  const [note, setNote] = useState("");
  const chosen = packages.find((row) => row.id === productId) ?? null;
  const free = chosen?.priceFils === 0;
  const method = free || payment === "later" ? null : payment;
  // When it runs (#ai-feedback 2026-10-26, card #1): today, or after the package that runs now.
  const start = usePackageStart(businessId, participantId, productId);
  const [startToday, setStartToday] = useState(false);
  const waitsFor = start.data?.waitsFor ?? null;
  const days = start.data ? (waitsFor && !startToday ? start.data : start.data.today) : null;
  const gets = chosen && days
    ? chosen.membership
      ? `a membership. The first month is from ${formatDayKey(days.startsOn)} to ${formatDayKey(days.endsOn)}, then it renews`
      : packPeriodLine({ credits: chosen.credits, unlimited: chosen.unlimited, startsOn: days.startsOn, endsOn: days.endsOn })
    : null;

  // The API refused the intro offer for this person: the owner may sell it anyway, on purpose.
  const notNew = isApiError(sell.error) && sell.error.code === "NEW_CLIENTS_ONLY" && chosen?.newClientsOnly === true;

  function submit(overrideNewClientsOnly = false) {
    if (!chosen) return;
    sell.mutate(
      {
        participantId,
        productId: chosen.id,
        method,
        ...(method && note.trim() ? { note: note.trim() } : {}),
        ...(method && proof.proofId ? { proofId: proof.proofId } : {}),
        ...(overrideNewClientsOnly ? { overrideNewClientsOnly: true } : {}),
        ...(waitsFor && startToday ? { startToday: true } : {}),
        idempotencyKey,
      },
      {
        onSuccess: () => {
          showToast(
            free
              ? `${name} has ${chosen.name}.`
              : method
                ? `${formatMoney(chosen.priceFils)} recorded. ${name} has ${chosen.name}.`
                : `${name} has ${chosen.name}. ${formatMoney(chosen.priceFils)} to pay.`,
          );
          onClose();
        },
      },
    );
  }

  const label = !chosen
    ? "Record"
    : free
      ? "Give the package"
      : method
        ? `Record ${formatMoney(chosen.priceFils)}`
        : "Add the package, to pay later";

  return (
    <Panel
      open
      onClose={onClose}
      title={`Record a payment for ${name}`}
      subtitle="A new package"
      animate={animate}
      actions={
        <>
          <Button disabled={!chosen || notNew || (method !== null && proof.uploading)} loading={sell.isPending} onClick={() => submit()}>
            {label}
          </Button>
          <Button variant="secondary" onClick={onBack ?? onClose}>
            {onBack ? "Back" : "Cancel"}
          </Button>
        </>
      }
    >
      {catalog.isLoading && !catalog.data ? (
        <SkeletonList rows={2} />
      ) : catalog.error && !catalog.data ? (
        <ErrorState error={catalog.error} onRetry={() => void catalog.refetch()} />
      ) : packages.length === 0 ? (
        <EmptyState title="No packages yet" message="Add a package in Packages and rules first. Then you can record a payment for it here." />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            {shown.length > 0 ? (
              <RadioGroup
                legend="Package"
                options={shown.map((row) => ({ value: row.id, label: row.name, hint: row.hint }))}
                value={productId}
                onChange={(id) => {
                  sell.reset();
                  setProductId(id);
                  setStartToday(false);
                }}
              />
            ) : (
              <p className="text-[14px] text-ink-2">
                No package is for {name}'s {word("group")}. Show the packages for other {word("group", { plural: true })}, or add one in Packages and rules.
              </p>
            )}
            {otherCount > 0 && !showAll ? (
              <Button variant="ghost" size="sm" className="self-start" onClick={() => setShowAll(true)}>
                Show {otherCount} {otherCount === 1 ? "package" : "packages"} for other {word("group", { plural: true })}
              </Button>
            ) : null}
          </div>
          {chosen && waitsFor && start.data ? (
            <RadioGroup<"after" | "today">
              legend="Starts"
              options={[
                {
                  value: "after",
                  label: `After ${waitsFor.name} ends`,
                  hint: `${waitsFor.name} ends on ${formatDayKey(waitsFor.endsOn)}. This package runs from ${formatDayKey(start.data.startsOn)} to ${formatDayKey(start.data.endsOn)}.`,
                },
                {
                  value: "today",
                  label: "Today",
                  hint: `From ${formatDayKey(start.data.today.startsOn)} to ${formatDayKey(start.data.today.endsOn)}. Both packages run at the same time.`,
                },
              ]}
              value={startToday ? "today" : "after"}
              onChange={(value) => setStartToday(value === "today")}
            />
          ) : null}
          {chosen && !free ? (
            <RadioGroup<PaymentChoice>
              legend="Payment"
              options={[
                { value: "cash", label: "Paid in cash" },
                { value: "transfer", label: "Paid by bank transfer" },
                {
                  value: "later",
                  label: "Not paid yet",
                  hint: `The package shows as owed. ${memberName} can pay in the app, or you record the payment later.`,
                },
              ]}
              value={payment}
              onChange={setPayment}
            />
          ) : null}
          {method ? (
            <Field
              label="Note, optional"
              hint={`${memberName} does not see this. It shows in Payments.`}
              dir="auto"
              value={note}
              maxLength={200}
              placeholder={payment === "transfer" ? "Transfer reference" : "Receipt number"}
              onChange={(event) => setNote(event.target.value)}
            />
          ) : null}
          {method ? <ProofField upload={proof} memberName={memberName} /> : null}
          {chosen ? (
            <p className="rounded-card bg-accent-surface px-4 py-3 text-[15px] text-ink" aria-live="polite">
              {name} gets {gets ?? `${chosen.name}`}.{" "}
              {free
                ? "There is nothing to pay."
                : method
                  ? `You record ${formatMoney(chosen.priceFils)} ${PAID_WORDS[method]}.`
                  : `${formatMoney(chosen.priceFils)} stays owed until it is paid.`}{" "}
              {memberName} gets a message.
            </p>
          ) : null}
          {notNew && chosen ? (
            <Notice
              tone="warning"
              title={`${memberName} is not a new ${word("member")}`}
              action={
                <Button variant="secondary" loading={sell.isPending} disabled={method !== null && proof.uploading} onClick={() => submit(true)}>
                  Add it anyway
                </Button>
              }
            >
              {chosen.name} is only for a first purchase. {memberName} bought a package or came to a session here before. You can
              still add it. Teamna keeps a record that you chose to.
            </Notice>
          ) : sell.error ? (
            <InlineError error={sell.error} />
          ) : null}
        </div>
      )}
    </Panel>
  );
}

/**
 * Record a cash or bank transfer payment for a package that is owed: a starting balance, a renewal,
 * or a package sold to pay later. The "owed package" step of Record a payment (`record-payment.tsx`).
 * It adds no sessions; it marks the package paid and puts the money in Payments, with an optional
 * proof of payment. Same idempotency rule as the sale.
 */
export function OwedPaymentPanel({
  businessId,
  participantId,
  name,
  memberName,
  owed,
  timezone,
  onBack,
  animate = true,
  onClose,
}: {
  businessId: string;
  participantId: string;
  name: string;
  memberName: string;
  owed: OwedBalance;
  timezone: string | undefined;
  /** Back to "What is the payment for?". */
  onBack?: (() => void) | undefined;
  animate?: boolean;
  onClose: () => void;
}) {
  const record = useRecordPayment(businessId);
  const proof = useProofUpload(businessId);
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [method, setMethod] = useState<ManualPaymentMethod>("cash");
  const [note, setNote] = useState("");

  return (
    <Panel
      open
      onClose={onClose}
      title={`Record a payment for ${name}`}
      subtitle="A package that is owed"
      animate={animate}
      actions={
        <>
          <Button
            loading={record.isPending}
            disabled={proof.uploading}
            onClick={() =>
              record.mutate(
                {
                  participantId,
                  purchaseId: owed.purchaseId,
                  method,
                  ...(note.trim() ? { note: note.trim() } : {}),
                  ...(proof.proofId ? { proofId: proof.proofId } : {}),
                  idempotencyKey,
                },
                {
                  onSuccess: () => {
                    showToast(`${formatMoney(owed.amountFils)} recorded for ${name}`);
                    onClose();
                  },
                },
              )
            }
          >
            Record {formatMoney(owed.amountFils)}
          </Button>
          <Button variant="secondary" onClick={onBack ?? onClose}>
            {onBack ? "Back" : "Cancel"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div>
          <p className="text-[15px] font-semibold text-ink">
            {owed.packageName}, {formatMoney(owed.amountFils)}
          </p>
          <p className="text-[14px] text-ink-2">{owedWhen(owed, timezone)}.</p>
        </div>
        <RadioGroup<ManualPaymentMethod>
          legend="How did they pay?"
          options={[
            { value: "cash", label: "Cash" },
            { value: "transfer", label: "Bank transfer" },
          ]}
          value={method}
          onChange={setMethod}
        />
        <Field
          label="Note, optional"
          hint={`${memberName} does not see this. It shows in Payments.`}
          dir="auto"
          value={note}
          maxLength={200}
          placeholder={method === "transfer" ? "Transfer reference" : "Receipt number"}
          onChange={(event) => setNote(event.target.value)}
        />
        <ProofField upload={proof} memberName={memberName} />
        <p className="rounded-card bg-accent-surface px-4 py-3 text-[15px] text-ink" aria-live="polite">
          The package is marked paid. The sessions do not change. {memberName} gets a message.
        </p>
        {record.error ? <InlineError error={record.error} /> : null}
      </div>
    </Panel>
  );
}

/** When an owed package was due: the period of a renewal, or the day of the sale. */
export function owedWhen(row: OwedBalance, timezone: string | undefined): string {
  return row.renewsOn ? `For the period that renews on ${formatDate(`${row.renewsOn}T12:00:00.000Z`, timezone)}` : `Sold on ${formatDate(row.purchasedAt, timezone)}`;
}

/** One thing a payment link pays: an owed package or a payment request. */
export type PayLinkItem = { key: string; what: string; amountFils: number; url: string };

export const REQUEST_STATUS: Record<OwnerPaymentRequest["status"], { label: string; tone: "late" | "confirmed" | "quiet" | "cancelled" }> = {
  pending: { label: "Not paid", tone: "late" },
  overdue: { label: "Late", tone: "cancelled" },
  paid: { label: "Paid", tone: "confirmed" },
  void: { label: "Cancelled", tone: "quiet" },
};

/** The family's one-off requests: open ones with their link, and the last paid or cancelled ones. */
export function PaymentRequestList({
  requests,
  timezone,
  onLink,
  onCancel,
}: {
  requests: OwnerPaymentRequest[];
  timezone: string | undefined;
  onLink: (request: OwnerPaymentRequest) => void;
  /** Missing: no Cancel the request (the front desk, T1). */
  onCancel?: (request: OwnerPaymentRequest) => void;
}) {
  return (
    <div>
      <h3 className="text-[16px]">Payment requests</h3>
      <ul className="mt-2 divide-y divide-line rounded-card border border-line">
        {requests.map((request) => {
          const open = request.status === "pending" || request.status === "overdue";
          const status = REQUEST_STATUS[request.status];
          return (
            <li key={request.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-2">
                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-2 font-semibold text-ink">
                    <span dir="auto">{request.description}</span>
                    <Badge tone={status.tone}>{status.label}</Badge>
                  </span>
                  <span className="block text-[13px] text-ink-2">
                    {formatMoney(request.amountFils)}
                    {request.participantName ? `, for ${request.participantName}` : ""}.{" "}
                    {request.status === "paid" && request.paidAt
                      ? `Paid ${formatDate(request.paidAt, timezone)}.`
                      : open
                        ? `Pay by ${formatDate(`${request.dueOn}T12:00:00.000Z`, timezone)}.`
                        : ""}
                  </span>
                </span>
              </div>
              {open ? (
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => onLink(request)}>
                    Payment link
                  </Button>
                  {onCancel ? (
                    <Button variant="ghost" onClick={() => onCancel(request)}>
                      Cancel the request
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** "150" or "150.5" as fils. Null for anything that is not a positive amount with at most 2 decimals. */
export function parseAmountFils(text: string): number | null {
  const clean = text.trim().replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(clean)) return null;
  const fils = Math.round(Number(clean) * 100);
  return fils > 0 ? fils : null;
}

/** Stripe refuses a card payment under AED 2.00 (seen on the sandbox, 2026-10-08). */
const MIN_REQUEST_FILS = 200;
const MAX_REQUEST_FILS = 10_000_000;

/**
 * Ask the family for an amount that is not a package: a swim cap, a trial week, an exam fee. The
 * request goes on the family's account. The family pays it in the app, or with a payment link the
 * owner sends, which needs no sign-in. After the request is made, the panel shows the link.
 */
export function RequestPaymentPanel({
  businessId,
  participantId,
  name,
  memberName,
  businessName,
  cardPayments,
  comingSoon = false,
  onClose,
}: {
  businessId: string;
  participantId: string;
  name: string;
  memberName: string;
  businessName: string;
  cardPayments: boolean;
  /** Teamna has card payments off for all businesses (Control Center switch). */
  comingSoon?: boolean;
  onClose: () => void;
}) {
  const create = useCreatePaymentRequest(businessId);
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [amount, setAmount] = useState("");
  const [what, setWhat] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [made, setMade] = useState<OwnerPaymentRequest | null>(null);
  const fils = parseAmountFils(amount);
  const amountError =
    amount.trim() === ""
      ? undefined
      : fils === null
        ? "Write an amount in AED, for example 150 or 150.50."
        : fils < MIN_REQUEST_FILS
          ? "The smallest amount is AED 2."
          : fils > MAX_REQUEST_FILS
            ? "The largest amount is AED 100,000."
            : undefined;
  const ready = fils !== null && !amountError && what.trim().length >= 2;

  if (made?.payLink) {
    return (
      <PayLinkPanel
        items={[{ key: made.id, what: made.description, amountFils: made.amountFils, url: made.payLink }]}
        memberName={memberName}
        businessName={businessName}
        title="Request made"
        onClose={onClose}
      />
    );
  }

  function submit() {
    if (!ready || fils === null) return;
    create.mutate(
      { participantId, amountFils: fils, description: what.trim(), ...(dueOn ? { dueOn } : {}), idempotencyKey },
      {
        onSuccess: (profile) => {
          // Newest first: the request just made is the first open one with this description.
          const request = profile.requests.find((row) => row.description === what.trim() && row.payLink) ?? null;
          showToast(`${formatMoney(fils)} requested from ${memberName}`);
          if (request) setMade(request);
          else onClose();
        },
      },
    );
  }

  return (
    <Panel
      open
      onClose={onClose}
      title={`Request a payment for ${name}`}
      actions={
        cardPayments ? (
          <>
            <Button disabled={!ready} loading={create.isPending} onClick={submit}>
              {fils !== null && !amountError ? `Request ${formatMoney(fils)}` : "Request"}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {comingSoon ? (
        <Notice tone="info" title="Payment requests are coming soon">
          A payment request is paid by card, and card payments are not available in Teamna yet. Until then, record cash and bank
          transfer payments.
        </Notice>
      ) : !cardPayments ? (
        <Notice
          tone="warning"
          title="Card payments are not on yet"
          action={
            <ButtonLink variant="secondary" href="/owner/setup/get-paid">
              Get paid by card
            </ButtonLink>
          }
        >
          A payment request is paid by card. Connect your business to Stripe first.
        </Notice>
      ) : (
        <div className="flex flex-col gap-5">
          <Field
            label="Amount in AED"
            inputMode="decimal"
            autoComplete="off"
            value={amount}
            placeholder="150"
            error={amountError}
            onChange={(event) => setAmount(event.target.value)}
          />
          <Field
            label="What is it for"
            hint={`${memberName} sees this on the payment page and on the receipt.`}
            dir="auto"
            value={what}
            maxLength={80}
            placeholder="Trial week"
            onChange={(event) => setWhat(event.target.value)}
          />
          <Field
            label="Pay by, optional"
            hint="Today when you leave it empty."
            type="date"
            value={dueOn}
            onChange={(event) => setDueOn(event.target.value)}
          />
          <p className="rounded-card bg-accent-surface px-4 py-3 text-[15px] text-ink" aria-live="polite">
            {fils !== null && !amountError && what.trim().length >= 2
              ? `${memberName} pays ${formatMoney(fils)} for ${what.trim()}. `
              : ""}
            You get a payment link to send. {memberName} pays by card with the link, without signing in, or in
            the Teamna app. The sessions do not change.
          </p>
          {create.error ? <InlineError error={create.error} /> : null}
        </div>
      )}
    </Panel>
  );
}

/**
 * The payment link for one or more things to pay, to copy or to send on WhatsApp. Anyone with the
 * link can pay; the payment always goes on the family's account.
 */
export function PayLinkPanel({
  items,
  memberName,
  businessName,
  title = "Payment link",
  onClose,
}: {
  items: PayLinkItem[];
  memberName: string;
  businessName: string;
  title?: string;
  onClose: () => void;
}) {
  return (
    <Panel
      open
      onClose={onClose}
      title={title}
      actions={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        {items.length > 1 ? (
          <p className="text-[15px] text-ink-2">
            Send these {items.length} links to {memberName}, one for each thing to pay. They pay by card and do not need
            to sign in. Each link closes when its payment arrives.
          </p>
        ) : (
          <p className="text-[15px] text-ink-2">
            Send this link to {memberName}. They pay by card and do not need to sign in. The link closes when the
            payment arrives.
          </p>
        )}
        {items.map((item) => (
          <PayLinkBox key={item.key} item={item} businessName={businessName} />
        ))}
        <p className="text-[13px] text-ink-3">
          Anyone with {items.length > 1 ? "a link" : "the link"} can pay it, so send {items.length > 1 ? "them" : "it"} only to{" "}
          {memberName}. The payment always goes on their account.
        </p>
      </div>
    </Panel>
  );
}

function PayLinkBox({ item, businessName }: { item: PayLinkItem; businessName: string }) {
  const field = useRef<HTMLInputElement>(null);
  const [manual, setManual] = useState(false);
  useEffect(() => {
    if (!manual) return;
    field.current?.focus();
    field.current?.select();
  }, [manual]);
  const message = payLinkMessage({ businessName, amount: formatMoney(item.amountFils), what: item.what, url: item.url });

  /** The same fallback as the invite link: a browser that refuses the clipboard selects the link instead. */
  async function copy() {
    try {
      if (!navigator.clipboard) throw new Error("No clipboard");
      await navigator.clipboard.writeText(item.url);
      showToast("Link copied");
    } catch {
      setManual(true);
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-card border border-line px-4 py-4">
      <p className="text-[15px] font-semibold text-ink" dir="auto">
        {item.what}, {formatMoney(item.amountFils)}
      </p>
      <CellField
        readOnly
        ref={field}
        value={item.url}
        label={`Payment link for ${item.what}`}
        onFocus={(event) => event.currentTarget.select()}
        onClick={(event) => event.currentTarget.select()}
      />
      {manual ? <p className="text-[13px] text-ink-2">Your browser does not let Teamna copy. Select the link and copy it.</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => void copy()}>
          <span className="inline-flex items-center gap-2">
            <CopyIcon size={16} /> Copy link
          </span>
        </Button>
        <ButtonLink variant="whatsapp" href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer">
          Send on WhatsApp
        </ButtonLink>
      </div>
    </div>
  );
}

/** Cancel an open request. Its link closes at once; the family can no longer pay it. */
export function CancelRequestPanel({
  businessId,
  participantId,
  request,
  memberName,
  onClose,
}: {
  businessId: string;
  participantId: string;
  request: OwnerPaymentRequest;
  memberName: string;
  onClose: () => void;
}) {
  const cancel = useCancelPaymentRequest(businessId);
  return (
    <Panel
      open
      onClose={onClose}
      title="Cancel the request?"
      actions={
        <>
          <Button
            variant="danger"
            loading={cancel.isPending}
            onClick={() =>
              cancel.mutate(
                { participantId, requestId: request.id },
                {
                  onSuccess: () => {
                    showToast("Request cancelled");
                    onClose();
                  },
                },
              )
            }
          >
            Cancel the request
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Keep it
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[15px] text-ink">
          {request.description}, {formatMoney(request.amountFils)}. The payment link closes and {memberName} can no longer
          pay it, in the app or with the link.
        </p>
        {cancel.error ? <InlineError error={cancel.error} /> : null}
      </div>
    </Panel>
  );
}

/** How far ahead "Book into a session" looks. Two weeks covers "next Tuesday" and the one after. */
const BOOK_AHEAD_DAYS = 14;

/**
 * Book one participant into a session from their profile. The same booking as "Add a participant"
 * on the schedule session panel (same route, same rules): it takes one session from their balance,
 * and the member does not need to have joined yet. Sessions of the participant's own group come
 * first; full ones show but cannot be picked.
 */
export function BookPanel({
  businessId,
  participant,
  bookedSessionIds,
  timezone,
  onClose,
}: {
  businessId: string;
  participant: OwnerParticipant;
  bookedSessionIds: string[];
  timezone: string | undefined;
  onClose: () => void;
}) {
  const { label } = useLabels();
  // Fixed when the panel opens, so the query key does not change on every render.
  const [range] = useState(() => {
    const now = new Date();
    return { now, from: now.toISOString(), to: new Date(now.getTime() + BOOK_AHEAD_DAYS * 86_400_000).toISOString() };
  });
  const sessions = useOwnerSessions({ businessId, from: range.from, to: range.to });
  const add = useAddParticipantToSession(businessId);
  const [sessionId, setSessionId] = useState("");
  const name = participant.displayName;

  const booked = new Set(bookedSessionIds);
  const options = (sessions.data ?? [])
    .filter((row) => row.status === "scheduled" && Date.parse(row.startsAt) > range.now.getTime() && !booked.has(row.id))
    .sort(
      (a, b) =>
        Number(b.groupId === participant.groupId) - Number(a.groupId === participant.groupId) ||
        a.startsAt.localeCompare(b.startsAt),
    );
  const chosen = options.find((row) => row.id === sessionId) ?? null;

  return (
    <Panel
      open
      onClose={onClose}
      title={`Book ${name} into a session`}
      subtitle={`The next ${BOOK_AHEAD_DAYS} days.`}
      actions={
        <>
          <Button
            disabled={!chosen || isSessionFull(chosen)}
            loading={add.isPending}
            onClick={() =>
              add.mutate(
                { sessionId, participantId: participant.id },
                {
                  onSuccess: (result) => {
                    showToast(`${name} is on the session. ${result.sessionsLeft} ${result.sessionsLeft === 1 ? "session" : "sessions"} left.`);
                    onClose();
                  },
                },
              )
            }
          >
            Book
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <p className="pb-3 text-[14px] text-ink-2">This takes one session from {name}'s balance.</p>
      {sessions.isLoading && !sessions.data ? (
        <SkeletonList rows={2} />
      ) : sessions.error && !sessions.data ? (
        <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
      ) : options.length === 0 ? (
        <p className="text-[14px] text-ink-2">No sessions to book in the next {BOOK_AHEAD_DAYS} days.</p>
      ) : (
        <Select label="Session to book" className="w-full" value={sessionId} onChange={(event) => setSessionId(event.target.value)}>
          <option value="">Choose a session</option>
          {options.map((row) => (
            <option key={row.id} value={row.id} disabled={isSessionFull(row)}>
              {formatDateTime(row.startsAt, timezone)}, {row.groupName}
              {row.spotsLeft === null ? "" : row.spotsLeft <= 0 ? " (full)" : ` (${row.spotsLeft} free)`}
            </option>
          ))}
        </Select>
      )}
      {participant.groupId === null ? (
        <p className="pt-3 text-[13px] text-ink-3">
          {name} is not in a {label("group")} yet, so every session shows.
        </p>
      ) : null}
      {add.error ? (
        <div className="mt-3">
          <InlineError error={add.error} />
        </div>
      ) : null}
    </Panel>
  );
}

export function CancelPanel({
  businessId,
  booking,
  name,
  memberName,
  timezone,
  onClose,
}: {
  businessId: string;
  booking: UpcomingBooking;
  name: string;
  memberName: string;
  timezone: string | undefined;
  onClose: () => void;
}) {
  const cancel = useBusinessCancelBooking(businessId);
  return (
    <Panel
      open
      onClose={onClose}
      title={`Take ${name} off this session?`}
      subtitle={`${booking.groupName}, ${formatDateTime(booking.startsAt, timezone)}`}
      actions={
        <>
          <Button
            variant="danger"
            loading={cancel.isPending}
            onClick={() =>
              cancel.mutate(
                { bookingId: booking.bookingId },
                {
                  onSuccess: (result) => {
                    showToast(
                      result.sessionsReturned > 0
                        ? `${name} is off the session. 1 session returned.`
                        : `${name} is off the session.`,
                    );
                    onClose();
                  },
                },
              )
            }
          >
            Cancel the booking
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Keep it
          </Button>
        </>
      }
    >
      <Notice tone="info" title="What happens">
        The business cancels, so the session goes back to {name}'s balance, whatever the cancel time. {memberName}{" "}
        gets a message that says so.
      </Notice>
      {cancel.error ? (
        <div className="mt-3">
          <InlineError error={cancel.error} />
        </div>
      ) : null}
    </Panel>
  );
}

export function ledgerColumns(timezone: string | undefined): DataTableColumn<LedgerLine>[] {
  return [
    {
      id: "date",
      header: "Date",
      hideOnPhone: true,
      cell: (line) => <span className="whitespace-nowrap">{formatDate(line.createdAt, timezone)}</span>,
    },
    {
      id: "line",
      header: "What happened",
      cell: (line) => (
        <span className="flex flex-col">
          <span>{line.label}</span>
          <span className="text-[12px] text-ink-3 md:hidden">{formatDate(line.createdAt, timezone)}</span>
        </span>
      ),
    },
    {
      id: "delta",
      header: "Sessions",
      align: "end",
      cell: (line) => (
        <span className={`font-semibold tabular-nums ${line.delta > 0 ? "text-confirmed" : line.delta < 0 ? "text-cancelled" : ""}`}>
          {line.delta > 0 ? `+${line.delta}` : line.delta}
        </span>
      ),
    },
  ];
}
